// Active OpsAudits listing for the import wizard's audit-selection step.
// Classic page-window pagination (matching Optro's own list tables) rather
// than the work-step listing's infinite scroll: each page replaces the list,
// with server-side search. Selection lives in the wizard context, so checked
// audits survive page changes. Generation-guarded against superseded fetches.
// Source: GET /work-steps/external/audits (Active, non-template audits only).
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFastApi } from "../api/fastapi";
import {
  ListOptroOpsAuditsResponseSchema,
  OptroOpsAuditSummary,
} from "../models/audit/workStep";
import { useDebounce } from "./useDebounce";

export function useOptroImportOpsAudits(search: string) {
  const debouncedSearch = useDebounce(search, 300);

  // 1-based page window.
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [opsAudits, setOpsAudits] = useState<OptroOpsAuditSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // False until the very first fetch settles — distinguishes the first-ever
  // load (full interstitial) from a search/page refetch (dimmed table).
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Incremented on every fetch (and unmount) so a slow in-flight response
  // can't stomp the state of a newer page/search.
  const generationRef = useRef(0);
  // Snapshot of the query inputs, to reset the page window to 1 when the
  // search term or page size changes without issuing a wasted fetch for the
  // stale page number.
  const lastQueryRef = useRef({ search: debouncedSearch, rowsPerPage });

  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const queryChanged =
      lastQueryRef.current.search !== debouncedSearch ||
      lastQueryRef.current.rowsPerPage !== rowsPerPage;
    lastQueryRef.current = { search: debouncedSearch, rowsPerPage };
    if (queryChanged && page !== 1) {
      // The fetch happens on the effect re-run the reset triggers.
      setPage(1);
      return;
    }

    generationRef.current += 1;
    const generation = generationRef.current;

    const fetchPage = async () => {
      setLoading(true);
      setError(false);
      try {
        const params = new URLSearchParams({
          search: debouncedSearch,
          limit: String(rowsPerPage),
          offset: String((page - 1) * rowsPerPage),
        });
        const response = await fetchFastApi({
          method: "GET",
          path: `/work-steps/external/audits?${params.toString()}`,
        });
        if (generationRef.current !== generation) return;
        const parsed = ListOptroOpsAuditsResponseSchema.parse(response);
        setOpsAudits(parsed.opsAudits);
        setTotal(parsed.total);
      } catch (err) {
        if (generationRef.current !== generation) return;
        console.error("Failed to load Optro audits:", err);
        setError(true);
      } finally {
        if (generationRef.current === generation) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };

    void fetchPage();
  }, [debouncedSearch, rowsPerPage, page, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    opsAudits,
    total,
    loading,
    hasLoadedOnce,
    error,
    retry,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  };
}
